import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";

type PdfPasswordModalProps = {
  open: boolean;
  fileName: string;
  isInvalid: boolean;
  onSubmit: (password: string) => void;
  onCancel: () => void;
};

export function PdfPasswordModal({
  open,
  fileName,
  isInvalid,
  onSubmit,
  onCancel,
}: PdfPasswordModalProps) {
  const [password, setPassword] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset password field each time the modal opens / a new attempt starts.
  useEffect(() => {
    if (open) {
      setPassword("");
      // Focus after the next paint so the element is visible.
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open, isInvalid]);

  if (!open) return null;

  const shortName =
    fileName.length > 48 ? `…${fileName.slice(-45)}` : fileName;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // The modal is often mounted inside another <form> (business KYC docs, financing
    // docs). React bubbles synthetic submit events through the
    // component tree — even across portals — so stop it here or the parent
    // form's onSubmit fires too and tears down the upload in progress.
    e.stopPropagation();
    const trimmed = password.trim();
    if (trimmed) onSubmit(trimmed);
  };

  // Portal to <body> so the modal's <form> is never nested inside a parent
  // <form> in the DOM and isn't affected by ancestor stacking/overflow.
  return createPortal(
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      {/* Panel */}
      <div className="w-full max-w-sm rounded-2xl border border-line bg-surface-raised p-6 shadow-xl">
        <h2 className="text-base font-semibold text-ink">
          Password-protected PDF
        </h2>

        <p className="mt-2 text-sm text-muted">
          {isInvalid ? (
            <>
              Incorrect password for <span className="font-medium text-ink">"{shortName}"</span>.
              Please try again.
            </>
          ) : (
            <>
              <span className="font-medium text-ink">"{shortName}"</span> is
              password protected. Enter the PDF password to continue uploading.
            </>
          )}
        </p>

        <form className="mt-4 space-y-4" onSubmit={handleSubmit}>
          <Input
            ref={inputRef}
            label="PDF password"
            type="password"
            autoComplete="off"
            value={password}
            error={isInvalid ? "Incorrect password" : undefined}
            onChange={(e) => setPassword(e.target.value)}
          />
          <div className="flex gap-3">
            <Button type="submit" variant="primary" className="flex-1" disabled={!password.trim()}>
              Unlock &amp; upload
            </Button>
            <Button type="button" variant="secondary" className="flex-1" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
